import AppRoutes from "AppRoutes";
import { createLogger } from "logging";
import { MeetingModel } from "models/MeetingModel";
import { useLayout, useMeeting } from "providers";
import { useTranslations } from "providers";
import { useBoard } from "providers";
import React, { PropsWithChildren, useCallback } from "react";
import { Item, Menu, Separator, TriggerEvent, useContextMenu } from "react-contexify";

import { MeetingsContextMenuContext } from "./MeetingsContextMenuContext";

const menuId = "meetings-context-menu";

const logger = createLogger("MeetingsContextMenuProvider");

export const MeetingsContextMenuProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const i18n = useTranslations();

  const { setSelectMeetingId, setConfirmDeleteMeeting } = useMeeting();
  const { board } = useBoard();
  const { show } = useContextMenu({ id: menuId });
  const { handleNavClick } = useLayout();

  const onClickMeeting = useCallback(
    (item: MeetingModel) => {
      const url = AppRoutes.toBoardMeeting(item.id, board!);
      handleNavClick(url);
    },
    [board, handleNavClick]
  );

  const onClickAgendaItems = useCallback(
    (item: MeetingModel) => {
      const url = AppRoutes.toBoardMeetingAgendaItems(item.id, board!);
      handleNavClick(url);
    },
    [board, handleNavClick]
  );

  const onClickParticipants = useCallback(
    (item: MeetingModel) => {
      const url = AppRoutes.toBoardMeetingParticipants(item.id, board!);
      handleNavClick(url);
    },
    [board, handleNavClick]
  );

  /*
  const onClickTasks = useCallback(
    (item: MeetingModel) => {
      const url = AppRoutes.toTasks(item.id, board!);
      handleNavClick(url);
    },
    [board, handleNavClick]
  );
  */

  const onClickDocuments = useCallback(
    (item: MeetingModel) => {
      const url = AppRoutes.toBoardMeetingDocuments(item.id, board!);
      handleNavClick(url);
    },
    [board, handleNavClick]
  );

  const onClickDelete = useCallback(
    (item: MeetingModel) => {
      setConfirmDeleteMeeting({ id: item.id, title: item.title });
    },
    [setConfirmDeleteMeeting]
  );

  const onClickViewHistory = useCallback(
    (item: MeetingModel) => {
      const url = AppRoutes.toBackendAuditLog("Meeting", item.id.toString());
      handleNavClick(url);
    },
    [handleNavClick]
  );

  const onClickOneDriveSync = useCallback(
    (item: MeetingModel) => {
      const url = AppRoutes.toBoardMeetingOneDriveSync(item.id, board!);
      handleNavClick(url);
    },
    [board, handleNavClick]
  );

  const isDisabledViewHistory = (item: MeetingModel) => {
    if (!item) return true;
    return !item.canViewHistory;
  };

  const isDisabledDelete = (item: MeetingModel) => {
    if (!item) return true;
    return !item.canDelete;
  };

  const showContextMenu = (event: TriggerEvent, item: MeetingModel) => {
    logger.debug("showContextMenu(): ", item);
    setSelectMeetingId(item.id);
    show({ event, id: menuId, props: item });
  };

  return (
    <MeetingsContextMenuContext.Provider
      value={{
        showContextMenu,
      }}
    >
      <Menu id={menuId} theme="sbb">
        <Item onClick={(e) => onClickMeeting(e.props)}>{i18n.Actions_View_Meeting}</Item>
        <Item onClick={(e) => onClickAgendaItems(e.props)}>{i18n.Actions_View_AgendaItems}</Item>
        <Item onClick={(e) => onClickParticipants(e.props)}>{i18n.Actions_View_Participants}</Item>
        <Item onClick={(e) => onClickDocuments(e.props)}>{i18n.Actions_View_Documents}</Item>
        <Item onClick={(e) => onClickOneDriveSync(e.props)}>{i18n.Actions_View_OneDriveSync}</Item>

        <Separator />

        <Item onClick={(e) => onClickDelete(e.props)} disabled={(e) => isDisabledDelete(e.props)}>
          {i18n.Actions_Delete_Meeting}
        </Item>
        <Item onClick={(e) => onClickViewHistory(e.props)} disabled={(e) => isDisabledViewHistory(e.props)}>
          {i18n.Actions_View_History}
        </Item>
      </Menu>

      {children}
    </MeetingsContextMenuContext.Provider>
  );
};
